import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";

const longBodies: Record<string, string[]> = {
  "z-01": [
    "当一段内容在短时间内被大量转发，「主角到底是谁」往往成为比内容本身更受关注的问题。然而现实里，从「这个人看起来很眼熟」到「我们可以确认他就是当事人」，中间隔着一条远比想象更长的路。",
    "第一步通常是片段式识别。有人凭口音、穿着或画面里一闪而过的细节，把主角与某个公开人物对号入座。这类推断看似具体，却往往只建立在单一路径上，一旦换一个视角便不再成立。",
    "第二步进入信息搜集。网友会沿着职业、学校、居住城市等线索展开排查，翻找历史账号、获奖名单或工商登记。这一阶段产出大量「疑似」结论，但多数缺乏可被第三方验证的锚点。",
    "真正决定身份能否被确认的，是能否找到多条互相独立又彼此印证的线索。我们建议以「三条独立线索同时指向同一人」作为基本门槛：一条来自公开可查的记录，一条来自可对质的当事人周边，一条来自可复核的时间或地点信息。",
    "需要强调的是，即使满足上述门槛，也仅代表「高度疑似」，而不等于「已经定性」。人物身份确认应当是排除合理怀疑的结果，而非热搜加持后的群体狂欢。",
    "这也是我们坚持给每条线索标注来源层级的原因——让读者自己判断证据的成色，而不是由我们替你做决定。",
  ],
  "z-02": [
    "几乎每一个走红的反差事件，都能追溯到某个明确的首次曝光节点。理解这一源头，是还原整条传播链条的起点。",
    "首发通常出现在规模有限的社群或评论区，影响范围小，语气也更接近「随手分享」。此时内容还未被贴上标签，人们关注的往往是事件本身，而非背后的人物身份。",
    "随后的搬运是第一次跃迁。搬运号或营销账号会为内容重新起标题、加背景音乐、补充所谓「内幕」，这一过程常常在不经意间放大了情绪，也压缩了事实。",
    "当话题冲上热搜或进入大号视野，二次加工便集中出现：当事人身份被猜测、片段被拼接、历史内容被考古。至此，信息开始与原始事实逐渐脱离。",
    "还原传播路径的意义，不在于追求一种「绝对真相」，而在于帮助我们识别哪一步开始偏离。理解了路径，你就不会再把热度等同于真实，也不会把晚到的、更冷静的声音误认为心虚。",
  ],
  "z-03": [
    "围绕当事人身份的证据五花八门，但它们的可信度差别极大。把证据按来源分级，是理性追踪的前提。",
    "第一类是可公开检索的客观记录，例如学籍公示、工商登记、获奖公告。这类证据存在官方背书的可能，可信度较高，但需要注意时效与同名问题。",
    "第二类是当事人或其亲友的直接发声。直接发声通常可信，但也要考虑表述情绪与立场倾向；当双方说法冲突时，应并列呈现而非偏信一方。",
    "第三类是私聊截图与「同班同学」爆料。这类证据的最大问题在于无法独立验证——截图可以伪造，身份可以冒充。我们将其划入「低可信待核实」层级，仅在出现多条独立互证的佐证时才予以参考。",
    "在整套分级里，我们的底线是：单一截图不构成证据链，情绪化的转发更不能替代核实。越是劲爆的说法，越值得你用同样的力气去验证。",
  ],
  "z-04": [
    "把「主角」与「当事人」区分开，是理解许多反差事件的第一步，却常常被忽略。",
    "主角，通常指被镜头和话题聚焦的那个人——他是内容叙事的中心，也是大众讨论指向的靶心。但主角只代表「被呈现的角色」，并不自动等同于事件里真实的核心行动者。",
    "当事人，则涵盖了更完整的涉事范围：亲历事件的人、主动爆料的匿名者、被卷入的周边人物，甚至传播链条上的搬运者，都可能被归入广义的当事人。",
    "当大众把「主角」当作唯一的「当事人」来审判时，最容易被误伤的反而是那些只在画面里短暂出现、却并未做错什么的人。",
    "因此我们提倡一个更谨慎的表述习惯：先说「被广泛指向的主角」，再说「有待确认的当事人」，最后才谈「证据链支持的涉事主体」。给身份一个阶梯，就是给理性留出空间。",
  ],
  "z-05": [
    "我们把若干类似事件的发酵过程抽象为一条可复用的时间轴，帮助读者理解情绪如何先于事实抵达。",
    "前 6 小时是首发与初步扩散。信息在小范围内流动，讨论相对就事论事，此时的提问大多是「这是真的吗」。",
    "6 到 24 小时进入放大阶段。搬运号介入、热搜排序上升，标签被贴牢，评论区开始划分阵营。有趣的是，越是缺少官方信息，人们越倾向于用「我相信谁」来代替「事实是什么」。",
    "24 到 48 小时是站队与质疑并存的阶段。新的截图不断涌现，真假混杂；当事人若回应，往往因为措辞不够「完美」而被进一步放大。",
    "48 到 72 小时是降温与分化的节点。冷静的声音开始回归，原始信源被重新检索，一些「铁证」被发现存在剪辑或断章取义的嫌疑。",
    "我们整理这条时间轴的用意，不是预言结果，而是提供一个参照：当讨论进行到某一步时，理性旁观者最该问的不是「站谁」，而是「现在还有多少信息是我没核实过的」。",
  ],
  "z-06": [
    "每一个走红的主角背后，几乎都牵连着一张或简单或复杂的关系网。看清这张网，才能识别争议背后的真实结构。",
    "最核心的关系是当事人与爆料者的关系。对方是亲眼所见的知情者，还是仅凭二手转述的远距离传播者，直接决定了信息的初始质量。",
    "其次是当事人与 MCN、签约公司、运营团队的关系。当内容走红带来明显的流量收益时，是否存在机构推动或剧本设计，往往是判断事件真伪的关键变量。",
    "再往外一圈是模仿者与「同款素人」。热点之下，有人刻意复刻人设蹭热度，也有人因「长相相似」被无辜卷入。分辨他们，需要比对历史内容与注册时间。",
    "理解这张关系网，并不会让真相自动浮现，但它能帮你识别哪些讨论是真实冲突、哪些只是流量生意——这是比追到某个具体身份更有价值的判断力。",
  ],
  "z-07": [
    "热点之下，冒充当事人或蹭热度的高仿账号总是层出不穷。掌握几招，就能快速排除绝大多数冒牌货。",
    "第一看注册时间。真正的当事人账号通常早于事件出现；若账号在走红当天或前几天才创建，冒充可能性极大。",
    "第二看历史内容。持续更新的账号拥有大量「过去」，冒充者往往内容单薄、语焉不详，甚至直接复制搬运他人素材。",
    "第三看认证主体。企业号、蓝 V 或平台认证能提供一定背书；无认证、隐私全关、粉丝数异常膨胀的账号需要提高警惕。",
    "第四看互动对象。真实的当事人会与长期互动的朋友、同事或客户有可追溯的交集；冒充者通常只能单向输出，无法对旧细节作出回应。",
    "第五看信息互斥点。若对方给出的细节与公开记录存在硬性矛盾（如时间、地点、职业不符），基本可以判定为高仿。把这几条过一遍，你就不会再被「自称当事人」牵着走。",
  ],
  "z-08": [
    "一旦我们先站了队，之后看到的每一条新证据，都会被自动代入早已写好的剧本。这是人类大脑的出厂设置，也是身份问题中最难克服的障碍。",
    "确认偏误是最常见的表现：我们倾向于寻找支持自己预设的证据，同时无视或贬低反对的声音。评论区里那些「果然如此」的感叹，多半是确认偏误在工作。",
    "聚光灯效应则让我们高估事件被关注的程度——当事人似乎时刻处于聚光灯下、任何回应都会被逐字审判，这其实是被放大的错觉。",
    "群体极化进一步放大这些偏差：在同温层里，观点会越来越极端，理性中立的声音反而显得「不合群」而被排挤。",
    "对抗这些偏误不需要高深的技巧，只需要一个习惯：在发表结论之前，先问自己——如果我的判断是错的，我需要看到什么样的证据才会改变主意？愿意为了答案调整立场，才是真正的清醒。",
  ],
};

const intro: Record<string, string> = {
  "z-01":
    "当一段反差内容走红，大众最关心的问题往往不是内容真假，而是「被指向的那个人到底是谁」。本文复盘一条身份线索从出现到被确认通常会经历的全部环节。",
  "z-02":
    "从首发帖到热搜，一条线索如何在社交网络上逐级扩散并逐渐偏离原意？我们把典型过程整理成完整传播路径。",
  "z-03":
    "私聊截图、学籍信息、同班同学发声——这些证据哪个更可信？本文为常见的身份证据划分可信层级。",
  "z-04":
    "主角是被镜头呈现的人，当事人则可能涵盖爆料者与传播者。厘清两者的边界，是理性吃瓜的第一步。",
  "z-05":
    "一组合成事件样本被拆解成六个发酵阶段，说明情绪如何先于事实抵达，也提醒你何时该按下暂停键。",
  "z-06":
    "当事人、爆料者、MCN、搬运号与同款素人之间如何相连？这份文字关系图谱帮你识别真假争议。",
  "z-07":
    "热点下总有人自称当事人。五条实操方法，帮你快速分辨「同一人」还是「蹭热度」的高仿号。",
  "z-08":
    "确认偏误、聚光灯效应与群体极化如何扭曲我们对身份问题的判断？本文给出主动规避的思维方式。",
};

const conclusions: Record<string, string> = {
  "z-01": "身份确认是一条需要耐心与证据的漫漫长路。我们的建议始终朴素：在没有多条独立交叉验证之前，先别急着把「疑似」读成「定案」。",
  "z-02": "理解传播路径，就是理解信息为何失真。下次看到劲爆内容，不妨先问一句：它最初是从哪里、以什么状态出现的？",
  "z-03": "证据分级的最终目的，是让我们在面对任何惊人说法时都能保持同等的审慎。能被验证的才值得相信，这本身就是最硬的立场。",
  "z-04": "给身份一个清晰的阶梯，就是给理性留出空间。区分主角与当事人，是每一个围观者都能做到的克制。",
  "z-05": "复盘时间轴的终点不是给出答案，而是建立参照系。下一次情绪上涌时，你已知道哪个节点最该保持清醒。",
  "z-06": "关系网的清晰度，决定了我们判断的成色。比起急着站队，先看清这张网，是更聪明的围观方式。",
  "z-07": "识别高仿账号并不需要专业工具，掌握几条朴素的规则就足以过滤绝大多数噪音。少上一当，也是一种胜利。",
  "z-08": "真正的清醒，是在热度退去后依然愿意修正自己的判断。愿我们在每次围观里，都能保留那份愿意改口的勇气。",
};

export default function ZixunDetail() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
        <p className="font-heading text-6xl font-bold text-primary-200">404</p>
        <h1 className="mt-4 text-2xl font-bold text-foreground-950">未找到这篇文章</h1>
        <p className="mt-2 text-foreground-600">内容可能已下线或地址有误。</p>
        <Link to="/zixun" className="mt-6 rounded-md bg-primary-600 px-6 py-2.5 text-sm text-background-50">
          返回资讯列表
        </Link>
      </div>
    );
  }

  const body = longBodies[article.id] || longBodies["z-01"];
  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="bg-background-50">
      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
        {/* breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/zixun" className="hover:text-primary-700">事件溯源</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{article.category}</span>
        </nav>

        {/* title */}
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-600 px-2.5 py-0.5 text-xs text-background-50">
              {article.category}
            </span>
            {article.tags.map((t) => (
              <span key={t} className="rounded-full bg-background-100 px-3 py-0.5 text-xs text-foreground-600">
                # {t}
              </span>
            ))}
          </div>
          <h1 className="font-heading mt-4 text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600">{intro[article.id]}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-edit-2-line" /> {article.author}
            </span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" /> {article.time}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" /> {article.reads} 阅读
            </span>
          </div>
        </div>

        {/* body */}
        <article className="mt-8">
          {body.map((para, i) => (
            <p key={i} className="mb-5 text-[15px] leading-8 text-foreground-800">
              {para}
            </p>
          ))}
        </article>

        {/* conclusion callout */}
        <div className="mt-6 rounded-lg border-l-4 border-primary-500 bg-background-100 p-5">
          <p className="font-heading text-sm font-bold text-foreground-950">编辑小结</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground-700">{conclusions[article.id]}</p>
        </div>

        {/* disclaimer */}
        <div className="mt-8 flex items-start gap-3 rounded-lg bg-secondary-100 p-5">
          <i className="ri-information-line mt-0.5 text-lg text-secondary-700" />
          <p className="text-sm leading-relaxed text-foreground-700">
            本文由本站编辑基于公开信息撰写，仅用于信息整理与方法讨论，不针对任何真实个体下结论，也不构成对任何事件的定性。请理性看待网络讨论。
          </p>
        </div>
      </div>

      {/* related */}
      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950">相关来源梳理</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/zixun/${r.id}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="text-xs text-primary-600">{r.category}</span>
                <h3 className="font-heading mt-2 text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {r.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{r.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}